/**
 * =============================================================================
 * IDS CRM Web - Delete, on a ticket page and a job page
 * =============================================================================
 *
 * Gareth, 13 October 2026: managers and admins need to delete tickets and
 * jobs, to tidy up the test ones. The server always allowed it (DELETE
 * /tickets/:id and DELETE /tasks/:id, manager and admin only) and has the
 * pay safeguards; there was no button.
 *
 * The button asks first, then deletes. The server refuses where pay is
 * involved, and its own sentence is handed to the page to show:
 *  - a ticket holding a call-out work log (move the log first; a paid one can
 *    never go, only be closed);
 *  - a job with any work logged, or with Log Work part saved (cancel it).
 * Nothing here decides any of that.
 *
 * A delete hides the record from every list; it stays in the database.
 * =============================================================================
 */

import React, { useState } from 'react';
import { useConfirm } from './ConfirmDialog';
import './DeleteRecordButton.css';

/** Who the server lets delete a ticket or a job (ROLES.MANAGEMENT). */
export const canDeleteRecords = (role) => ['manager', 'admin'].includes(role);

export const deleteNoSignalMessage = (kind) => `The ${kind} could not be deleted. Check your signal and try again.`;
export const deleteFailedMessage = (kind) => `The ${kind} could not be deleted. Try again.`;

/**
 * @param {object} props
 * @param {'ticket'|'job'} props.kind the word used in the question and the messages
 * @param {string} props.reference what it is called, e.g. TCK-2026-0102
 * @param {string} props.endpoint where to send the DELETE
 * @param {Function} props.authFetch
 * @param {() => void} props.onDeleted called once it is deleted (the page leaves for its list)
 * @param {(sentence: ?string) => void} props.onRefused the server's reason, or null when a new try starts
 */
function DeleteRecordButton({ kind, reference, endpoint, authFetch, onDeleted, onRefused }) {
  const { confirmElement, requestConfirm } = useConfirm();
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    setDeleting(true);
    onRefused(null);
    let response;
    try {
      response = await authFetch(endpoint, { method: 'DELETE' });
    } catch (err) {
      onRefused(deleteNoSignalMessage(kind));
      setDeleting(false);
      return;
    }
    if (!response || !response.ok) {
      const data = response ? await response.json().catch(() => ({})) : {};
      onRefused(data.message || deleteFailedMessage(kind));
      setDeleting(false);
      return;
    }
    onDeleted();
  };

  return (
    <>
      <button
        type="button"
        className="btn-action danger delete-record-button"
        disabled={deleting}
        title={`Delete this ${kind} (managers and admins)`}
        onClick={() => requestConfirm({
          title: `Delete ${reference}?`,
          message: `This ${kind} will be removed from every list and cannot be brought back from the screen. `
            + `A ${kind} with paid or logged work on it is refused, and you are told why.`,
          confirmLabel: `Delete ${kind}`,
          cancelLabel: 'Keep it',
          danger: true,
          onConfirm: remove
        })}
      >
        {deleting ? 'Deleting...' : 'Delete'}
      </button>
      {confirmElement}
    </>
  );
}

export default DeleteRecordButton;
